<template>
  <ul class="layout-menu">
    <template v-for="(item, i) in model" :key="item.label">
      <app-menu-item v-if="!item.separator" :item="item" :index="i"/>
      <li v-if="item.separator" class="menu-separator"></li>
    </template>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSuperAdmin } from '@/composables/use-super-admin'
import { useOrganizationsStore } from '@/stores/organizations'
import { storeToRefs } from 'pinia'
import { useOrgRouteContext } from '@/composables/use-org-route-context'

import AppMenuItem from './AppMenuItem.vue'

const route = useRoute()
const { isSuperAdmin } = useSuperAdmin()
const { activeOrganization } = storeToRefs(useOrganizationsStore())
const { isOnOrgRoute } = useOrgRouteContext()

interface MenuEntry {
  label: string
  icon?: string
  to?: string
}

interface MenuSection {
  label: string
  items: MenuEntry[]
  separator: boolean
}

const model = computed<MenuSection[]>(() => {
  const org = activeOrganization.value
  const orgSlug = org?.slug ?? ''

  // Super admins browsing an org via /super-admin/org/<slug> keep that prefix;
  // everyone else uses the plain /org/<slug> tree (or the root when no org).
  const isSaOrgRoute = isSuperAdmin.value && org && route.path.startsWith('/super-admin/org/')
  const routePrefix = orgSlug
    ? `${isSaOrgRoute ? '/super-admin/org' : '/org'}/${orgSlug}`
    : '/'

  const items: MenuSection[] = []

  // Only show org nav items when on an org route
  if (isOnOrgRoute.value && org) {
    items.push({
      label: 'Home',
      items: [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-home',
          to: routePrefix
        },
        {
          label: 'Company Metrics',
          icon: 'pi pi-fw pi-chart-bar',
          to: `${routePrefix}/company-metrics`
        },
        {
          label: 'Branding',
          icon: 'pi pi-fw pi-palette',
          to: `${routePrefix}/branding`
        },
        {
          label: 'Member Experience',
          icon: 'pi pi-fw pi-sparkles',
          to: `${routePrefix}/member-experience`
        },
        {
          label: 'Members',
          icon: 'pi pi-fw pi-users',
          to: `${routePrefix}/members`
        },
        {
          label: 'Subscription',
          icon: 'pi pi-fw pi-credit-card',
          to: `${routePrefix}/subscription`
        },
        {
          label: 'Integrations',
          icon: 'pi pi-fw pi-link',
          to: `${routePrefix}/integrations`
        },
        {
          label: 'Notes',
          icon: 'pi pi-fw pi-comment',
          to: `${routePrefix}/notes`
        },
      ],
      separator: false
    })
  }

  if (isSuperAdmin.value) {
    items.push({
      label: 'Administration',
      items: [
        { label: 'Organizations', icon: 'pi pi-fw pi-building', to: '/super-admin' },
        { label: 'User Subscriptions', icon: 'pi pi-fw pi-id-card', to: '/super-admin/user-subscriptions' },
        { label: 'Member Activity', icon: 'pi pi-fw pi-table', to: '/super-admin/member-activity' },
        { label: 'Send Email', icon: 'pi pi-fw pi-envelope', to: '/super-admin/send-email' },
        { label: 'Notes', icon: 'pi pi-fw pi-comment', to: '/super-admin/notes' },
        { label: 'Coach earnings', icon: 'pi pi-fw pi-wallet', to: '/super-admin/coach-earnings' },
        { label: 'Supplement Catalog', icon: 'pi pi-fw pi-book', to: '/super-admin/supplement-catalog' },
        { label: 'Supplement Signups', icon: 'pi pi-fw pi-inbox', to: '/super-admin/supplement-signups' },
      ],
      separator: false
    })
  }

  return items
})
</script>

<style lang="scss" scoped></style>
